/**
 * scheduler/BackgroundScheduler.ts — configurable background jobs.
 *
 * Roamer Core™ schedules recurring work (refresh weather, refresh currency,
 * cleanup caches, health sweeps, …). Jobs are declarative: id + interval +
 * run(). Intervals are configurable at runtime; jobs can be triggered manually
 * (`runNow`) which is also how deterministic tests drive them. Failures are
 * recorded, never thrown — one broken job cannot take down the scheduler.
 */

export interface BackgroundJob {
  id: string;
  label: string;
  intervalMs: number;
  /** Job body. May be async. Errors are captured into stats. */
  run: () => Promise<void> | void;
  enabled?: boolean;
}

export interface JobStats {
  id: string;
  label: string;
  intervalMs: number;
  enabled: boolean;
  runs: number;
  failures: number;
  lastRunAt: number | null;
  lastDurationMs: number | null;
  lastError: string | null;
}

interface JobEntry {
  job: BackgroundJob;
  enabled: boolean;
  timer: ReturnType<typeof setInterval> | null;
  runs: number;
  failures: number;
  lastRunAt: number | null;
  lastDurationMs: number | null;
  lastError: string | null;
}

export class BackgroundScheduler {
  private jobs = new Map<string, JobEntry>();
  private started = false;

  /** Register (or replace) a job. Timers only run after `start()`. */
  addJob(job: BackgroundJob): void {
    this.removeJob(job.id);
    const entry: JobEntry = {
      job,
      enabled: job.enabled !== false,
      timer: null,
      runs: 0,
      failures: 0,
      lastRunAt: null,
      lastDurationMs: null,
      lastError: null,
    };
    this.jobs.set(job.id, entry);
    if (this.started && entry.enabled) this.arm(entry);
  }

  removeJob(id: string): void {
    const entry = this.jobs.get(id);
    if (entry?.timer) clearInterval(entry.timer);
    this.jobs.delete(id);
  }

  /** Change a job's interval at runtime (configurable scheduling). */
  setInterval(id: string, intervalMs: number): boolean {
    const entry = this.jobs.get(id);
    if (!entry) return false;
    entry.job.intervalMs = intervalMs;
    if (entry.timer) {
      clearInterval(entry.timer);
      entry.timer = null;
      if (this.started && entry.enabled) this.arm(entry);
    }
    return true;
  }

  setEnabled(id: string, enabled: boolean): boolean {
    const entry = this.jobs.get(id);
    if (!entry) return false;
    entry.enabled = enabled;
    if (!enabled && entry.timer) {
      clearInterval(entry.timer);
      entry.timer = null;
    } else if (enabled && this.started && !entry.timer) {
      this.arm(entry);
    }
    return true;
  }

  /** Start all enabled timers. Idempotent. */
  start(): void {
    if (this.started) return;
    this.started = true;
    for (const entry of this.jobs.values()) {
      if (entry.enabled) this.arm(entry);
    }
  }

  /** Stop all timers (jobs stay registered). */
  stop(): void {
    this.started = false;
    for (const entry of this.jobs.values()) {
      if (entry.timer) {
        clearInterval(entry.timer);
        entry.timer = null;
      }
    }
  }

  /** Run one job immediately (also the deterministic path for tests). */
  async runNow(id: string): Promise<boolean> {
    const entry = this.jobs.get(id);
    if (!entry) return false;
    await this.execute(entry);
    return true;
  }

  private arm(entry: JobEntry): void {
    entry.timer = setInterval(() => {
      void this.execute(entry);
    }, entry.job.intervalMs);
  }

  private async execute(entry: JobEntry): Promise<void> {
    const started = Date.now();
    entry.runs += 1;
    entry.lastRunAt = started;
    try {
      await entry.job.run();
      entry.lastError = null;
    } catch (err) {
      entry.failures += 1;
      entry.lastError = err instanceof Error ? err.message : String(err);
    } finally {
      entry.lastDurationMs = Math.max(0, Date.now() - started);
    }
  }

  listJobs(): JobStats[] {
    return Array.from(this.jobs.values()).map((e) => ({
      id: e.job.id,
      label: e.job.label,
      intervalMs: e.job.intervalMs,
      enabled: e.enabled,
      runs: e.runs,
      failures: e.failures,
      lastRunAt: e.lastRunAt,
      lastDurationMs: e.lastDurationMs,
      lastError: e.lastError,
    }));
  }

  get isRunning(): boolean {
    return this.started;
  }
}
